import { useState } from "react";
import Modal from "../../../../../components/Modal/Modal";
import GridIcon from "../../../../../assets/svg/grid-icon";
import ListViewIcon from "../../../../../assets/svg/list-view-icon";
import CloseIcon from "../../../../../assets/svg/close-icon";
import { UserData } from "../utils/types";

interface OrganizationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizations: UserData["clients"];
}

const layoutOptions = [
  { type: "grid" as const, icon: GridIcon, label: "Grid view" },
  { type: "list" as const, icon: ListViewIcon, label: "List view" },
];

export const OrganizationsModal = ({
  open,
  onOpenChange,
  organizations = [],
}: OrganizationsModalProps) => {
  const [orgLayout, setOrgLayout] = useState<"grid" | "list">("grid");

  const handleOrgLayoutChange = (layout: "grid" | "list") => {
    setOrgLayout(layout);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={false}
      showCloseButton={false}
      contentClassName="bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg w-[400px] shadow-popper-blur-effect backdrop-blur-2xl !z-[99999999]"
      overlayClassName="!bg-black/60 !z-[9999999]"
      bodyClassName="!pl-6 !pr-10 !py-[18px] !relative"
    >
      <button
        className="absolute top-3 right-3 w-[16px] h-[16px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center z-10"
        onClick={() => onOpenChange(false)}
      >
        <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
      </button>
      <div className="flex flex-col gap-3 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-heading-text dark:text-heading-text-dk">
              Organisations
            </span>
            <span className="text-xxs font-semibold leading-0 text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk rounded-sm px-1 py-2">
              {organizations?.length || 0}
            </span>
          </div>

          <div className="flex items-center p-0.5 border border-border-gray dark:border-border-gray-dk rounded-sm">
            {layoutOptions?.map(({ type, icon: Icon, label }) => (
              <div
                key={type}
                className={`px-0.5 py-px cursor-pointer rounded-sm ${
                  orgLayout === type &&
                  "bg-verba-indigo dark:bg-verba-indigo-dk"
                }`}
                onClick={() => handleOrgLayoutChange(type)}
                title={label}
              >
                <Icon
                  className={`w-[18px] h-[18px] ${
                    orgLayout === type
                      ? "text-pure-white dark:text-pure-white-dk"
                      : "text-verba-indigo dark:text-verba-indigo-dk"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className={`flex ${
            orgLayout === "grid" ? "flex-wrap" : "flex-col"
          } gap-2 overflow-y-auto max-h-[calc(100dvh-225px)] pb-4`}
        >
          {organizations?.map((org, index) => (
            <div
              key={org.client_code || index}
              className="w-max flex items-center gap-1.5 px-1.5 py-1 bg-pure-white dark:bg-pure-white-dk rounded-md border border-border-gray dark:border-border-gray-dk"
            >
              <img
                src={org.client_logo}
                alt={org.client_name}
                className="w-4 h-4 object-cover shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <span className="text-sm text-heading-text dark:text-heading-text-dk truncate">
                {org.client_name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
